import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BadgeCheck, Quote, Star, X } from "lucide-react";
import { toast } from "sonner";
import { Chapter, EASE, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange, size = "h-4 w-4" }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        strokeWidth={1.5}
        data-testid={onChange ? `review-star-${n}` : undefined}
        className={`${size} transition-colors duration-200 ${onChange ? "cursor-pointer" : ""} ${
          n <= value ? "fill-[#F97316] text-[#F97316]" : "text-white/25"
        }`}
        onClick={onChange ? () => onChange(n) : undefined}
      />
    ))}
  </div>
);

const ReviewCard = ({ r, verified, badge, testid }) => (
  <div
    className="flex h-full w-[84%] shrink-0 snap-start flex-col rounded-2xl border border-white/10 bg-[#111111] p-8 transition-colors duration-500 hover:border-[#F97316]/40 sm:w-[47%] lg:w-[32%]"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <Quote className="h-6 w-6 text-[#F97316]" strokeWidth={1.5} />
      <Stars value={r.rating} />
    </div>
    <p className="mt-6 flex-1 text-sm leading-relaxed text-white/60 md:text-base">&ldquo;{r.review}&rdquo;</p>
    <div className="mt-8 border-t border-white/10 pt-5">
      <p className="font-syne text-base font-bold tracking-tight">{r.name}</p>
      <p className="mt-1 font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/35">
        {r.position} — {r.company}
      </p>
      {verified && (
        <p className="mt-3 flex items-center gap-1.5 font-spacemono text-[9px] uppercase tracking-[0.25em] text-[#F97316]/80">
          <BadgeCheck className="h-3 w-3" /> {badge}
        </p>
      )}
    </div>
  </div>
);

export default function Testimonials() {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", position: "", email: "", rating: 0, review: "" });
  const trackRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setReviews)
      .catch(() => {});
  }, []);

  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * 440, behavior: "smooth" });

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const field =
    "w-full border-b border-white/15 bg-transparent py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#F97316]";

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) {
      toast.error(t("reviews.modal.toastRating"));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(t("reviews.modal.toastSuccess"));
      setOpen(false);
      setForm({ name: "", company: "", position: "", email: "", rating: 0, review: "" });
      const fresh = await fetch(`${API}/reviews`);
      if (fresh.ok) setReviews(await fresh.json());
    } catch {
      toast.error(t("reviews.modal.toastError"));
    } finally {
      setSending(false);
    }
  };

  const items = reviews;
  const modal = t("reviews.modal");

  return (
    <section id="testimonials" className="py-28 md:py-40" data-testid="testimonials-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Chapter number="07" title={t("chapters.reviews")} />
        <FadeUp className="mb-14 flex flex-wrap items-end justify-between gap-8 md:mb-20">
          <div>
            <h2 className="max-w-3xl text-balance font-syne text-3xl font-bold leading-[1.12] tracking-tight sm:text-4xl lg:text-[3.4rem]" data-testid="testimonials-heading">
              {t("reviews.headingA")} <span className="text-[#F97316]">{t("reviews.headingB")}</span>
            </h2>
            {avg && (
              <p className="mt-5 flex items-center gap-3" data-testid="reviews-summary">
                <span className="font-syne text-2xl font-extrabold text-[#F97316]">{avg}</span>
                <Stars value={Math.round(avg)} />
                <span className="font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {reviews.length} {t("reviews.summarySuffix")}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={() => setOpen(true)}
            className="group flex items-center gap-3 rounded-full bg-[#F97316] px-7 py-3.5 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:bg-[#EA580C] active:scale-[0.96]"
            data-testid="leave-review-button"
          >
            {t("reviews.leaveButton")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </FadeUp>

        {reviews.length > 0 && (
          <FadeUp>
            <div className="relative" data-testid="reviews-carousel">
            <div
              ref={trackRef}
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="reviews-track"
            >
              {items.map((r, i) => (
                <ReviewCard
                  key={r.id || i}
                  r={r}
                  verified={reviews.length > 0}
                  badge={t("reviews.verifiedBadge")}
                  testid={`review-card-${i + 1}`}
                />
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3">
              <button
                onClick={() => scroll(-1)}
                aria-label="Previous reviews"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 hover:border-[#F97316] hover:bg-[#F97316]"
                data-testid="reviews-prev-button"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => scroll(1)}
                aria-label="Next reviews"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 hover:border-[#F97316] hover:bg-[#F97316]"
                data-testid="reviews-next-button"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          </FadeUp>
        )}

        {reviews.length === 0 && (
          <FadeUp>
            <div
              className="flex flex-col items-start gap-4 rounded-2xl border border-dashed border-white/15 p-10"
              data-testid="reviews-empty-state"
            >
              <Quote className="h-6 w-6 text-[#F97316]" strokeWidth={1.5} />
              <p className="max-w-md text-sm leading-relaxed text-white/50 md:text-base">{t("reviews.emptyState")}</p>
            </div>
          </FadeUp>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="review-modal"
          >
            <motion.div
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#111111] p-8 md:p-10"
              initial={{ opacity: 0, y: 44 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 44 }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-8 flex items-start justify-between">
                <div>
                  <h3 className="font-syne text-2xl font-bold tracking-tight">{modal.title}</h3>
                  <p className="mt-2 font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/35">
                    {modal.note}
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close review form"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 hover:border-[#F97316]"
                  data-testid="review-modal-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <form onSubmit={submit} className="flex flex-col" data-testid="review-form">
                <input type="text" required placeholder={modal.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="review-name-input" />
                <input type="text" required placeholder={modal.company} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={`${field} mt-5`} data-testid="review-company-input" />
                <input type="text" required placeholder={modal.position} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className={`${field} mt-5`} data-testid="review-position-input" />
                <input type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${field} mt-5`} data-testid="review-email-input" />
                <div className="mt-7 flex items-center justify-between">
                  <span className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/40">{modal.rating}</span>
                  <Stars value={form.rating} onChange={(rating) => setForm({ ...form, rating })} size="h-6 w-6" />
                </div>
                <textarea required rows={4} placeholder={modal.reviewPh} value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })} className={`${field} mt-5 resize-none`} data-testid="review-text-input" />
                <button
                  type="submit"
                  disabled={sending}
                  className="mt-9 flex w-fit items-center gap-3 rounded-full bg-[#F97316] px-8 py-4 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform,opacity] duration-300 hover:bg-[#EA580C] active:scale-[0.96] disabled:opacity-50"
                  data-testid="review-submit-button"
                >
                  {sending ? modal.sending : modal.submit}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
